import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, Bell } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../contexts/AuthContext'
import axiosInstance from '../../../services/axiosInstance'
import { isAdmin } from '../../../utils/roleUtils'
import ConfirmationModal from '../../modals/ConfirmationModal'
import './styles.scss'

export default function PageHeader({ title, subtitle, children, universityName: propUniversityName }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const universityName = propUniversityName || user?.organizationName || user?.teamName || 'University of Memphis'
  
  const [unreadCount, setUnreadCount] = useState(0)
  const [university, setUniversity] = useState(null)
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const [showLogoutModal, setShowLogoutModal] = useState(false)
  
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowProfileMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (isAdmin(user)) {
      fetchUnreadCount()
    }
    
    const fetchUniversity = () => {
      if (user?.teamId) {
        axiosInstance.get('/api/settings/university')
          .then(res => setUniversity(res.data))
          .catch(err => console.error("Failed to fetch university details", err))
      }
    }

    fetchUniversity()
    window.addEventListener('university-updated', fetchUniversity)
    return () => window.removeEventListener('university-updated', fetchUniversity)
  }, [user])

  const fetchUnreadCount = async () => {
    try {
      // Sum up unread messages from all applications
      const response = await axiosInstance.get('/api/applications/admin/all')
      if (response.data.success) {
        const apps = response.data.data
        const totalUnread = apps.reduce((sum, app) => sum + (app.unreadMessages || 0), 0)
        setUnreadCount(totalUnread)
      }
    } catch (err) {
      console.error("Failed to fetch unread count for badge", err)
    }
  }

  return (
    <header className="page-header-component">
      <div>
        <h1>{title}</h1>
        {subtitle && <p className="dash-subtitle">{subtitle}</p>}
      </div>
      <div className="dash-header__right">
        <div 
          className="dash-filter" 
          onClick={() => { if (isAdmin(user)) navigate('/settings') }}
          style={{ 
            cursor: isAdmin(user) ? 'pointer' : 'default', 
            pointerEvents: isAdmin(user) ? 'auto' : 'none', 
            display: 'flex', alignItems: 'center', gap: '8px' 
          }}
          title={isAdmin(user) ? "University Settings" : ""}
        >
          {university?.logoUrl && (
            <img 
              src={university.logoUrl} 
              alt="Logo" 
              style={{ width: '20px', height: '20px', objectFit: 'contain', borderRadius: '4px' }} 
            />
          )}
          <span>{universityName}</span>
        </div>

        {children}



        <div className="profile-menu-container" style={{ position: 'relative' }} ref={menuRef}>
          <button 
            className="profile-icon-btn" 
            onClick={() => setShowProfileMenu(!showProfileMenu)}
            style={{ 
              width: '36px', height: '36px', borderRadius: '50%', background: '#3b82f6', 
              color: 'white', border: 'none', cursor: 'pointer', display: 'flex', 
              alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' 
            }}
          >
            {(user?.fullName || user?.fullname) ? (user.fullName || user.fullname).charAt(0).toUpperCase() : '?'}
          </button>
          
          {showProfileMenu && (
            <div className="profile-dropdown" style={{
              position: 'absolute', top: '45px', right: '0', background: 'white',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '8px', border: '1px solid #e2e8f0', width: '200px', zIndex: 100,
              display: 'flex', flexDirection: 'column', padding: '8px 0'
            }}>
              <div style={{ padding: '8px 16px', borderBottom: '1px solid #e2e8f0', marginBottom: '8px' }}>
                <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.8rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.fullName || user?.fullname}</div>
                <div style={{ color: '#3b82f6', fontSize: '0.7rem', fontWeight: '500', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {user?.roles?.includes('SUPER_ADMIN') ? 'Super Admin' : user?.roles?.includes('ADMIN') ? 'Admin' : user?.roles?.includes('AGENT') ? 'Agent' : user?.roles?.includes('USER') ? 'Student' : 'Staff'}
                </div>
                <div style={{ color: '#64748b', fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
              </div>
              <Link 
                to={`/profile/${user?.id}`} 
                style={{ padding: '8px 16px', textDecoration: 'none', color: '#334155', fontSize: '0.9rem', display: 'block' }}
                onClick={() => setShowProfileMenu(false)}
              >
                View My Profile
              </Link>
              <button 
                onClick={() => { setShowProfileMenu(false); setShowLogoutModal(true); }} 
                style={{ padding: '8px 16px', border: 'none', background: 'none', color: '#dc2626', fontSize: '0.9rem', textAlign: 'left', cursor: 'pointer', display: 'block', width: '100%' }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={() => {
          setShowLogoutModal(false)
          logout()
          navigate('/')
        }}
        title="Sign Out"
        message="Are you sure you want to sign out?"
        confirmText="Sign Out"
        cancelText="Cancel"
      />
    </header>
  )
}
